import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "@/components/Reveal";
import { PORTFOLIO, PORTFOLIO_FILTERS } from "@/data/site";

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const items = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <FadeUp>
          <Overline>Selected Work</Overline>
        </FadeUp>
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-7xl leading-[0.98] tracking-tight">
            <MaskedLines lines={["PORTFOLIO."]} />
          </h2>
          <FadeUp delay={0.2}>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Portfolio filters">
              {PORTFOLIO_FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`portfolio-filter-${f.toLowerCase()}`}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] border transition-colors duration-300 ${
                    filter === f
                      ? "border-gold text-gold"
                      : "hairline text-ivory/60 hover:text-ivory"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </FadeUp>
        </div>

        <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.button
                layout
                key={p.title}
                data-testid={`portfolio-item-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                data-cursor="VIEW"
                onClick={() => setSelected(p)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="group relative block w-full mb-4 break-inside-avoid overflow-hidden border hairline text-left"
              >
                <img
                  src={p.img}
                  alt={`${p.title} — ${p.service}`}
                  className={`w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105 ${
                    p.tall ? "aspect-[3/4]" : "aspect-[4/3]"
                  }`}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {p.real && (
                  <span className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase text-gold bg-ink/60 backdrop-blur px-3 py-1.5 border border-gold/40">
                    Studio Work
                  </span>
                )}
                <div className="absolute bottom-0 inset-x-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{p.cat} — {p.service}</p>
                  <p className="mt-1 font-display text-xl text-ivory">{p.title}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="portfolio-lightbox"
            className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setSelected(null)}
          >
            <button
              data-testid="portfolio-lightbox-close"
              className="absolute top-6 right-6 text-ivory/70 hover:text-gold transition-colors p-2 z-10"
              aria-label="Close"
            >
              <X size={28} />
            </button>
            <motion.figure
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.img}
                alt={`${selected.title} — ${selected.service}`}
                className="w-full max-h-[78vh] object-contain border hairline"
              />
              <figcaption className="mt-4 flex items-baseline justify-between">
                <span className="font-display text-2xl text-ivory">{selected.title}</span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-bronze">
                  {selected.real ? "Studio Work — " : ""}{selected.cat} — {selected.service}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
